/**
 * Reines Kontakt-Datenmodell: Typen, Kontakttyp-Katalog und Anzeige-Helfer
 * ohne Abhängigkeit auf die Request-Schicht. Wird von contactsApi.ts
 * re-exportiert und kann in Tests ohne Router/Store geladen werden.
 */

export interface Contact {
  id?: number;
  idOrganization?: number;
  /**
   * Unternehmen der Organisation, dem der Kontakt gehört (09.09.26 V).
   * null = organisationsweit gepflegt und in jeder Unternehmens-Auswahl
   * sichtbar, z. B. Versicherungen oder Polizeidienststellen.
   */
  idCompany?: number | null;
  contactType: string | null;
  // Partnerstatus: aktive Partner der Organisation vs. nur angezeigte Kontakte
  isActive: boolean;
  // Zuständigkeit (Unfall, Geschäftsleitung, …); dasselbe Unternehmen darf je Zuständigkeit einen Eintrag haben
  responsibility: string | null;
  companyName: string | null;
  salutation: string | null;
  title: string | null;
  firstName: string | null;
  lastName: string | null;
  streetNo: string | null;
  zip: string | null;
  city: string | null;
  country: string | null;
  phone: string | null;
  mobile: string | null;
  fax: string | null;
  email: string | null;
  email2: string | null;
  email3: string | null;
  website: string | null;
  // Bankverbindung (lokale Felder, unabhängig von der Portal-Verknüpfung)
  accountHolder: string | null;
  iban: string | null;
  bic: string | null;
  bankName: string | null;
  notes: string | null;
  isFavorite: boolean;
  /** Standardrolle des Favoriten (Fall-Rolle, Kundenfeedback 18.10.2026 XII); nur über setContactFavorite änderbar. */
  favoriteRole?: string | null;
  // Portal-Verknüpfung (Freundesliste-Modus)
  idLinkedOrganization?: number | null;
  idLinkedDepartment?: number | null;
  linkedAt?: string | null;
  linkSource?: string | null;
  isPortalLinked?: boolean;
  /** Kundenfeedback 02.11.2026 (I): eigener Vermerk „Möchte keine KARS-Einladungen“; nur über setContactKarsInvitations änderbar. */
  noKarsInvitations?: boolean;
  /** Die Organisation des Kontakts hat Einladungen der eigenen Organisation selbst abbestellt. */
  karsInvitationsDeclined?: boolean;
  /** Kundenfeedback 03.11.2026 (I): Die E-Mail-Adresse hat sich über den Abmeldelink einer Einladungsmail abgemeldet (Zeitpunkt). */
  karsInvitationsOptedOutByMailAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

// ═══════════════════════════════════════════════════════════════════
// Contact types & display helpers
// ═══════════════════════════════════════════════════════════════════

export const CONTACT_TYPES = [
  // Feedback XX.4: Auch Kundenkontakte lassen sich hier verwalten.
  { value: "Kunde", label: "Kunde", icon: "customer", color: "#00796b" },
  { value: "Rechtsanwalt", label: "Rechtsanwalt", icon: "lawyer", color: "#1565c0" },
  { value: "Sachverständiger", label: "Sachverständiger", icon: "specialist", color: "#6a1b9a" },
  { value: "Werkstatt", label: "Werkstatt", icon: "wrench", color: "#e65100" },
  { value: "Abschleppdienst", label: "Abschleppdienst", icon: "towing-service", color: "#37474f" },
  { value: "Mietwagenfirma", label: "Mietwagenfirma", icon: "carRental", color: "#00838f" },
  { value: "Versicherung", label: "Versicherung", icon: "insurance", color: "#2e7d32" },
  { value: "Polizei", label: "Polizei", icon: "police", color: "#0d47a1" },
  { value: "Staatsanwaltschaft", label: "Staatsanwaltschaft", icon: "prosecutor", color: "#4e342e" },
  // Banken, Leasing- und Finanzierungsgesellschaften (Import Polz & Kollegen, 13.09.26).
  { value: "Bank", label: "Bank", icon: "bank", color: "#ad1457" },
  { value: "Sonstige", label: "Sonstige", icon: "contact", color: "#757575" },
] as const;

/** Vorschläge für die Zuständigkeit; freie Eingabe bleibt möglich. */
export const RESPONSIBILITY_SUGGESTIONS = ["Unfall", "Geschäftsleitung", "Buchhaltung", "Forderungen/Inkasso", "Verkauf"] as const;

export type ContactTypeInfo = { value: string; label: string; icon: string; color: string };

export function getContactTypeInfo(type: string | null): ContactTypeInfo {
  const found = CONTACT_TYPES.find((t) => t.value === type);
  if (found) return found as ContactTypeInfo;
  return CONTACT_TYPES[CONTACT_TYPES.length - 1] as ContactTypeInfo;
}

export function getContactDisplayName(contact: Contact): string {
  if (contact.companyName) return contact.companyName;
  const parts = [contact.firstName, contact.lastName].filter(Boolean);
  return parts.join(" ") || "Unbenannt";
}

export function emptyContact(): Contact {
  return {
    idCompany: null,
    contactType: null,
    isActive: true,
    responsibility: null,
    companyName: null,
    salutation: null,
    title: null,
    firstName: null,
    lastName: null,
    streetNo: null,
    zip: null,
    city: null,
    country: "Deutschland",
    phone: null,
    mobile: null,
    fax: null,
    email: null,
    email2: null,
    email3: null,
    website: null,
    accountHolder: null,
    iban: null,
    bic: null,
    bankName: null,
    notes: null,
    isFavorite: false,
  };
}

/**
 * Kundenfeedback 02.11.2026 (I): Der Kontakt möchte keine KARS-Einladungen –
 * eigener Vermerk oder Abbestellung durch seine Organisation.
 */
export function contactRefusesKarsInvitations (c: Pick<Contact, "noKarsInvitations" | "karsInvitationsDeclined" | "karsInvitationsOptedOutByMailAt"> | null | undefined): boolean {
  return !!(c?.noKarsInvitations || c?.karsInvitationsDeclined || c?.karsInvitationsOptedOutByMailAt);
}

/**
 * Kundenfeedback 03.11.2026 (I): „Wie wird das vermerkt?“ – Vermerk, wenn sich
 * die Adresse selbst über den Abmeldelink abgemeldet hat, z. B.
 * „per E-Mail abgemeldet am 03.11.2026“ (ohne Datum, wenn es fehlt/unlesbar ist).
 */
export function karsInvitationsOptOutByMailLabel (c: Pick<Contact, "karsInvitationsOptedOutByMailAt"> | null | undefined): string {
  const raw = c?.karsInvitationsOptedOutByMailAt;
  if (!raw) { return ""; }
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) { return "per E-Mail abgemeldet"; }
  return `per E-Mail abgemeldet am ${date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}`;
}
